import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  TableSortLabel,
} from "@mui/material";
import NoData from "../Nodata";
import Loader from "../Loader";
import { matchesColumnFilter } from "../../utils/tableUtils";

const DataTable = ({
  columns = [],
  data = [],
  visibleColumns = {},
  loading,
  onSort, // Optional: action function for sorting
  onFilter, // Optional: action function for filtering
}) => {
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // 🔍 Filter logic
  const filteredData = useMemo(() => {
    // If onFilter action function is provided, return data as-is (parent handles filtering)
    if (onFilter) {
      return data;
    }

    // Default filtering logic
    return data.filter(row => columns.every(col => matchesColumnFilter(col, row, filters)));
  }, [data, columns, filters, onFilter]);

  // 🔄 Sort logic
  const sortedAndFilteredData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return filteredData || [];
    }

    let result = [...filteredData];

    // If onSort action function is provided, return as-is (parent handles sorting)
    if (onSort) {
      return result;
    }

    // If no sort is applied, return filtered data as-is
    if (!sortConfig.key) {
      return result;
    }

    // Default sorting logic
    result.sort((a, b) => {
      const aValue = a?.[sortConfig.key];
      const bValue = b?.[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Convert to comparable values
      let compareA = aValue;
      let compareB = bValue;

      // If both are numbers, compare as numbers
      if (typeof aValue === "number" && typeof bValue === "number") {
        compareA = aValue;
        compareB = bValue;
      }
      // If both are booleans, compare as booleans
      else if (typeof aValue === "boolean" && typeof bValue === "boolean") {
        compareA = aValue ? 1 : 0;
        compareB = bValue ? 1 : 0;
      }
      // If both are strings, try date parsing first, then string comparison
      else if (typeof aValue === "string" && typeof bValue === "string") {
        const dateA = new Date(aValue);
        const dateB = new Date(bValue);
        // If both are valid dates, compare as dates
        if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
          compareA = dateA.getTime();
          compareB = dateB.getTime();
        } else {
          // Otherwise, compare as strings (case-insensitive)
          compareA = aValue.toLowerCase();
          compareB = bValue.toLowerCase();
        }
      }
      // Mixed types - convert to string for comparison
      else {
        compareA = String(aValue).toLowerCase();
        compareB = String(bValue).toLowerCase();
      }

      // Compare values
      if (compareA < compareB) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (compareA > compareB) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return result;
  }, [filteredData, sortConfig, onSort]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    // If onFilter action function is provided, call it
    if (onFilter) {
      onFilter(newFilters);
    }
  };

  const handleSort = key => {
    let newDirection = "asc";

    // If clicking the same column, toggle direction
    if (sortConfig.key === key) {
      newDirection = sortConfig.direction === "asc" ? "desc" : "asc";
    }
    // If clicking a different column, start with ascending
    else {
      newDirection = "asc";
    }

    const newSortConfig = { key, direction: newDirection };
    setSortConfig(newSortConfig);

    // If onSort action function is provided, call it
    if (onSort) {
      onSort(key, newDirection);
    }
  };

  // Filter columns based on visibility
  const visibleColumnsList = useMemo(() => {
    // If visibleColumns is empty or not provided, show all columns
    if (!visibleColumns || Object.keys(visibleColumns).length === 0) {
      return columns;
    }
    return columns.filter(col => visibleColumns[col.key] !== false);
  }, [columns, visibleColumns]);

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <TableContainer
        component={Paper}
        sx={{
          overflowX: "auto",
          backgroundColor: "#fff",
        }}
        className="rounded-lg border border-gray-200 shadow-sm"
      >
        <Table size="small" sx={{ minWidth: 900 }}>
          {/* ===== HEADER ===== */}
          <TableHead>
            {/* Column labels */}
            <TableRow className="bg-gray-600">
              {visibleColumnsList.map(col => (
                <TableCell key={col.key} sx={{ color: "#fff", fontWeight: 600, whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    active={sortConfig.key === col.key}
                    direction={sortConfig.key === col.key ? sortConfig.direction : "asc"}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSort(col.key);
                    }}
                    sx={{
                      color: "#fff !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        color: "#fff !important",
                        opacity: sortConfig.key === col.key ? 1 : 0.5,
                      },
                      "&:hover": {
                        color: "#fff !important",
                      },
                    }}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>

            {/* Filter row */}
            <TableRow sx={{ backgroundColor: "#fff" }}>
              {visibleColumnsList.map(col => (
                <TableCell key={col.key}>
                  {/* Show filter input by default, only hide if explicitly set to false */}
                  {col.filterable !== false ? (
                    <TextField
                      size="small"
                      placeholder={col.label}
                      value={filters[col.key] || ""}
                      onChange={e => handleFilterChange(col.key, e.target.value)}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "6px",
                          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                          "& fieldset": {
                            borderColor: "#d1d5db",
                            borderWidth: "1px",
                          },
                          "&:hover fieldset": {
                            borderColor: "#d1d5db",
                            borderWidth: "1px",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ff9800",
                            borderWidth: "2px",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                          },
                        },
                        "& .MuiInputBase-input": {
                          color: "#1f2937",
                          "&::placeholder": {
                            color: "#6b7280",
                            opacity: 1,
                          },
                        },
                      }}
                    />
                  ) : null}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* ===== BODY ===== */}
          <TableBody sx={{ backgroundColor: "#fff" }}>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumnsList.length}
                  align="center"
                  sx={{
                    py: 8,
                    minHeight: 200,
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <Loader />
                  </Box>
                </TableCell>
              </TableRow>
            ) : sortedAndFilteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumnsList.length} align="center">
                  <NoData />
                </TableCell>
              </TableRow>
            ) : (
              sortedAndFilteredData.map((row, rowIndex) => (
                <TableRow
                  key={row + rowIndex}
                  hover
                  sx={{
                    backgroundColor: "#fff",
                    "&:hover": {
                      backgroundColor: "#f9fafb",
                    },
                  }}
                >
                  {visibleColumnsList.map(col => {
                    const value = row?.[col.key];
                    return (
                      <TableCell key={col.key} sx={{ whiteSpace: "nowrap" }}>
                        {col.render ? col.render(value, row) : (value ?? "—")}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

DataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  visibleColumns: PropTypes.object,
  loading: PropTypes.bool,
  onSort: PropTypes.func, // Optional: (key, direction) => void
  onFilter: PropTypes.func, // Optional: (filters) => void
};

export default DataTable;
